import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  ArraySliceDO,
  AvailableAnswersDO,
  BlockScoresDO,
  ContinueSurveyDO,
  CustomerBlockDO,
  CustomerCategoryDO,
  CyFunLevel,
  DescriptionsDO,
  emptySALineData,
  FrameworkCodesDO,
  InternalAnswersDO,
  InternalFunctionsDO,
  OwnTargetDO,
  QuestionDO,
  RecordStringStringDO,
  SelfAssessmentLineChartDataDO,
  TargetScoreDO,
  TargetScoreRatingsDO,
} from 'src/components/visionx/selfAssessment/types';
import { VXCommentDO } from 'src/components/visionx/vx-comments/types';

interface SetUpdateAnswerPayload {
  blockId: string;
  categoryId: string;
  subCategoryId: string;
  questionId: string;
  indexInQuestionsArray: number;
  answerNumericValue: number;
  answerTextValue: number;
  customerAnswer: number;
}

interface UpdateValidationPayload {
  id: number;
  customerValidation: string;
}

export interface SelfAssessmentState {
  chartsData: SelfAssessmentLineChartDataDO;
  percentComplete: number;
  totalComplete: number[];
  questions: QuestionDO[];
  blockName: string;
  categoryName: string;
  sideNavPane: string;
  isDataNormalized: boolean;
  normalizedData: InternalFunctionsDO;
  categoryCodes: FrameworkCodesDO;
  targetScores: TargetScoreRatingsDO;
  blockTargetScores: RecordStringStringDO;
  answers: InternalAnswersDO;
  blockId: string;
  categoryIdToCategoryCode: RecordStringStringDO;
  blockNameToId: RecordStringStringDO;
  descriptions: RecordStringStringDO;
  questionnaireId: string;
  masterChartType: string;
  masterChartArraySlice: ArraySliceDO;
  availableDays: string[];
  blockScores: BlockScoresDO;
  comments: VXCommentDO[];
  continueSurvey: ContinueSurveyDO[];
  allCustomerBlocks: CustomerBlockDO[];
  allCustomerCategories: CustomerCategoryDO[];
  allCustomerOwnTargets: OwnTargetDO[];
  notDisplaySurveyIds: string[];
  normalizedSurveys: string[];
  endpointPrefix: string;
  availableAnswers: AvailableAnswersDO;
  newDescriptions: DescriptionsDO | undefined;
  riskCutOffPoint: number;
  cyFunLevel: CyFunLevel;
  targetScoreValues: TargetScoreDO[];
}

const initialState: SelfAssessmentState = {
  chartsData: { selfAssessment: emptySALineData, labels: {} },
  percentComplete: 0,
  totalComplete: [],
  questions: [],
  blockName: 'Overview',
  categoryName: '',
  sideNavPane: '',
  isDataNormalized: false,
  normalizedData: {},
  categoryCodes: {},
  targetScores: {},
  blockTargetScores: {},
  answers: {},
  blockId: '7',
  categoryIdToCategoryCode: {},
  blockNameToId: {},
  descriptions: {},
  questionnaireId: '',
  masterChartType: 'line',
  masterChartArraySlice: { start: 0, end: 30 },
  availableDays: [],
  blockScores: {
    customer: { labels: [], scores: [] },
    target: { labels: [], scores: [] },
  },
  comments: [],
  continueSurvey: [{ title: ' ', percentage: 0, id: '', percentComplete: 0 }],
  allCustomerBlocks: [],
  allCustomerCategories: [],
  allCustomerOwnTargets: [],
  notDisplaySurveyIds: [],
  normalizedSurveys: [],
  endpointPrefix: 'maturityassess', // selfassess || maturityassess (new),
  availableAnswers: {},
  newDescriptions: undefined,
  riskCutOffPoint: 2.5,
  cyFunLevel: 'NIST2',
  targetScoreValues: [
    { cyFunLevel: 'NIST2', targetScore: 3 },
    { cyFunLevel: 'BASIC', targetScore: 2.5 },
    { cyFunLevel: 'IMPORTANT', targetScore: 3 },
    { cyFunLevel: 'ESSENTIAL', targetScore: 3.5 },
  ],
};

export const selfAssessmentSlice = createSlice({
  name: 'selfAssessmentSlice',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setCyFunLevel(state, action: PayloadAction<CyFunLevel>) {
      state.cyFunLevel = action.payload;
    },
    setChartData(state, action: PayloadAction<SelfAssessmentLineChartDataDO>) {
      state.chartsData = action.payload;
    },
    setBlockNameToId(state, action: PayloadAction<RecordStringStringDO>) {
      state.blockNameToId = action.payload;
    },

    setQuestions(state, action: PayloadAction<QuestionDO[]>) {
      state.questions = action.payload;
    },

    setTotalComplete(state, action: PayloadAction<number[]>) {
      state.totalComplete = action.payload;
    },
    setUpdateAnswer(state, action: PayloadAction<SetUpdateAnswerPayload>) {
      const payload = action.payload;
      const customerAnswer: number = payload.answerNumericValue;
      state.questions[payload.indexInQuestionsArray]['question']['customerAnswer'] = customerAnswer;
      state.answers[payload.blockId][payload.categoryId][payload.subCategoryId][
        payload.questionId
      ] = customerAnswer;
    },
    updateValidation(state, action: PayloadAction<UpdateValidationPayload>) {
      const payload = action.payload;
      const id: number = payload.id;
      const customerValidation: string = payload.customerValidation;

      const question = state.questions.find((q) => (q.question.id as unknown as number) === id);
      if (question) {
        question.question.customerValidation = customerValidation;
      }
    },

    setIsDataNormalized(state, action: PayloadAction<boolean>) {
      state.isDataNormalized = action.payload;
    },
    setNormalizedData(state, action) {
      state.normalizedData = action.payload;
    },

    setBlockName(state, action: PayloadAction<string>) {
      state.blockName = action.payload;
    },
    setBlockId(state, action: PayloadAction<string>) {
      state.blockId = action.payload;
    },
    setCategoryName(state, action: PayloadAction<string>) {
      state.categoryName = state.categoryName === action.payload ? '' : action.payload;
    },
    setCategoryCodes(state, action: PayloadAction<FrameworkCodesDO>) {
      state.categoryCodes = action.payload;
    },
    setTargetScores(state, action: PayloadAction<TargetScoreRatingsDO>) {
      state.targetScores = action.payload;
    },
    setBlockTargetScores(state, action: PayloadAction<RecordStringStringDO>) {
      state.blockTargetScores = action.payload;
    },
    setAnswers(state, action: PayloadAction<InternalAnswersDO>) {
      state.answers = action.payload;
    },
    setCategoryIdToCategoryCode(state, action: PayloadAction<RecordStringStringDO>) {
      state.categoryIdToCategoryCode = action.payload;
    },
    setDescriptions(state, action: PayloadAction<RecordStringStringDO>) {
      state.descriptions = action.payload;
    },
    setQuestionnaireId(state, action: PayloadAction<string>) {
      state.questionnaireId = action.payload;
    },
    setMasterChartType(state, action: PayloadAction<string>) {
      state.masterChartType = action.payload;
    },
    setMasterChartArraySlice(state, action: PayloadAction<ArraySliceDO>) {
      state.masterChartArraySlice = action.payload;
    },
    setAvailableDays(state, action: PayloadAction<string[]>) {
      state.availableDays = action.payload;
    },
    setComments(state, action: PayloadAction<VXCommentDO[]>) {
      state.comments = action.payload;
    },
    setContinueSurvey(state, action: PayloadAction<ContinueSurveyDO[]>) {
      state.continueSurvey = action.payload;
    },
    setAllCustomerCategories(state, action: PayloadAction<CustomerCategoryDO[]>) {
      state.allCustomerCategories = action.payload;
    },

    setNotDisplayedSurveyIds(state, action: PayloadAction<string[]>) {
      state.notDisplaySurveyIds = action.payload;
    },
    updateNormalizedSurveys(state, action: PayloadAction<string>) {
      const isSurveyNormalized: boolean = state.normalizedSurveys.includes(action.payload);
      if (!isSurveyNormalized) {
        state.normalizedSurveys = [...state.normalizedSurveys, action.payload];
      }
    },
    resetNormalizedSurveys(state) {
      state.normalizedSurveys = [];
    },
    setAvailableAnswers(state, action: PayloadAction<AvailableAnswersDO>) {
      state.availableAnswers = action.payload;
    },
    setNewDescriptions(state, action: PayloadAction<DescriptionsDO>) {
      state.newDescriptions = action.payload;
    },
  },
});

export const {
  setCyFunLevel,
  setChartData,
  setQuestions,
  setIsDataNormalized,
  setNormalizedData,
  setBlockName,
  setBlockId,
  setCategoryName,
  setCategoryCodes,
  setTargetScores,
  setBlockTargetScores,
  setAnswers,
  setUpdateAnswer,
  setTotalComplete,
  setCategoryIdToCategoryCode,
  setBlockNameToId,
  setDescriptions,
  setQuestionnaireId,
  setMasterChartArraySlice,
  setMasterChartType,
  setAvailableDays,
  setComments,
  setContinueSurvey,
  setAllCustomerCategories,
  setNotDisplayedSurveyIds,
  updateNormalizedSurveys,
  setAvailableAnswers,
  setNewDescriptions,
  resetNormalizedSurveys,
  updateValidation,
} = selfAssessmentSlice.actions;

export default selfAssessmentSlice.reducer;
